import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { Loader } from '../../../../../components/Loader/Loader';
import {
  HTML_FIRST_PART_PAGE_CODE,
  HTML_LAST_PART_PAGE_CODE
} from '../../../../../components/HTMLEditorQuill/HTMLEditorConst';
import { uploadFileAllPath } from '../../../../../helpers/service/file';
import { getSettingsUploadFiles } from '../../../../../helpers/service/nobody/settings';
import {
  DEFAULT_GENERAL_MESSAGE_ATTACHMENT_SIZE_LIMIT_BYTES,
  formatGeneralMessageAttachmentSize,
  normalizeGeneralMessageAttachmentSizeLimit
} from '../../../../../helpers/generalMessageAttachments/generalMessageAttachmentSize';
import { EligibleSchool } from '../../../../../models/school';
import { Tournament } from '../../../../../models/tournament';
import { Attachment } from '../../../../../models/generalMessage';
import { AppUser } from '../../../../../views/App/App';
import { TournamentCustomNotificationTextForm } from '../CreateTournamentCustomNotificationsView/TournamentCustomNotificationTextForm';
import { TournamentCustomNotificationPreview } from '../CreateTournamentCustomNotificationsView/TournamentCustomNotificationPreview';
import '../TournamentNotificationWizard.scss';

enum MESSAGE_CREATING_STEPS {
  CONTENT = 'CONTENT',
  PREVIEW = 'PREVIEW'
}

interface Props {
  recipientDescription?: string;
  user: AppUser;
  schools: EligibleSchool[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  tournament?: Tournament;
  onDirtyChange?: (isDirty: boolean) => void;
}

interface MessageState {
  messageEmailSubject: string;
  messageEmailBody: string;
  messagePushTitle: string;
  messagePushBody: string;

  currentStep: number;
  isError: boolean;
  errorMessage: string;

  messageAttachments: Attachment[];
  isImageError: boolean;
  isLoading: boolean;
  filesSize: number;
  maxSizeUploadFile: number;

  isLoadingFile: boolean;
  isPushEditorEnabled: boolean;
  isEmailEditorEnabled: boolean;

  includeUnionAdmins: boolean;

  submitAction: 'SEND_NOW' | 'SCHEDULE';
  scheduleAt: string;
}

export class TournamentEligibleSchoolsCustomNotificationsView extends Component<Props, MessageState> {
  constructor(props: Props) {
    super(props);

    this.state = {
      currentStep: 0,
      isError: false,
      errorMessage: '',
      messageEmailSubject: '',
      messageEmailBody: '',
      messagePushTitle: '',
      messagePushBody: '',
      messageAttachments: [],
      isImageError: false,
      isLoading: false,
      filesSize: 0,
      maxSizeUploadFile: DEFAULT_GENERAL_MESSAGE_ATTACHMENT_SIZE_LIMIT_BYTES,
      isLoadingFile: false,
      isPushEditorEnabled: true,
      isEmailEditorEnabled: true,
      includeUnionAdmins: true,
      submitAction: 'SEND_NOW',
      scheduleAt: ''
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });
    this.setAdditionalItems();
  }

  componentDidUpdate(prevProps: Props, prevState: MessageState) {
    if (!this.props.onDirtyChange) return;
    const fields: Array<keyof MessageState> = [
      'messageEmailSubject',
      'messageEmailBody',
      'messagePushTitle',
      'messagePushBody',
      'messageAttachments',
      'isPushEditorEnabled',
      'isEmailEditorEnabled',
      'includeUnionAdmins',
      'submitAction',
      'scheduleAt'
    ];
    const wasDirty = fields.some(field => JSON.stringify(prevState[field]) !== JSON.stringify(this.state[field]));
    if (wasDirty) {
      const isDirty = Boolean(
        this.state.messageEmailSubject ||
          this.state.messageEmailBody ||
          this.state.messagePushTitle ||
          this.state.messagePushBody ||
          this.state.messageAttachments.length ||
          this.state.scheduleAt ||
          !this.state.isPushEditorEnabled ||
          !this.state.isEmailEditorEnabled ||
          !this.state.includeUnionAdmins ||
          this.state.submitAction !== 'SEND_NOW'
      );
      this.props.onDirtyChange(isDirty);
    }
  }

  setAdditionalItems() {
    const { user } = this.props;

    return getSettingsUploadFiles(user)
      .then(maxSizeUploadFile => {
        this.setState({
          maxSizeUploadFile,
          isLoading: false
        });

        return true;
      })
      .catch(error => {
        console.error(error);
        this.setState({
          isLoading: false
        });
        return true;
      });
  }

  onNextClick = () => {
    const {
      currentStep,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      messageEmailBody,
      messageEmailSubject,
      messagePushBody,
      messagePushTitle
    } = this.state;

    const steps = this.getSteps();
    const currentStepName = steps[currentStep];
    const isCurrentStepContent = currentStepName === MESSAGE_CREATING_STEPS.CONTENT;

    const isPushBodyExist = messagePushBody !== '';
    const isPushTitleExist = messagePushTitle !== '';
    const isEmailSubjectExist = messageEmailSubject !== '';
    const isEmailBodyExist = messageEmailBody !== '';

    const isShowPushError = (!isPushBodyExist || !isPushTitleExist) && isPushEditorEnabled;
    const isShowEmailError = (!isEmailSubjectExist || !isEmailBodyExist) && isEmailEditorEnabled;

    if (isCurrentStepContent && (isShowPushError || isShowEmailError)) {
      let errorText = '';

      switch (true) {
        case isShowPushError:
          errorText = 'Title or body for push content should be defined';
          break;
        case isShowEmailError:
          errorText = 'Subject or body for email content should be defined';
          break;
      }

      this.setState({
        isError: true,
        errorMessage: errorText
      });
    } else {
      this.setState(prevState => ({
        currentStep: prevState.currentStep + 1
      }));
    }
  };

  onPrevClick = () => {
    this.setState(prevState => ({
      currentStep: prevState.currentStep - 1
    }));
  };

  onEmailSubjectChange = event => {
    const value = event.target.value;

    this.setState({
      messageEmailSubject: value,
      isError: false,
      errorMessage: ''
    });
  };

  onEmailBodyEmptyTextChange = emptyText => {
    this.setState({
      messageEmailBody: emptyText,
      isError: false,
      errorMessage: ''
    });
  };

  onEmailBodyChange = editorState => {
    this.setState({
      messageEmailBody: editorState,
      isError: false,
      errorMessage: ''
    });
  };

  onPushTitleChange = event => {
    const value = event.target.value;

    this.setState({
      messagePushTitle: value,
      isError: false,
      errorMessage: ''
    });
  };

  onPushBodyChange = event => {
    const value = event.target.value;

    this.setState({
      messagePushBody: value,
      isError: false,
      errorMessage: ''
    });
  };

  onEmailAttachmentChange = (event): void => {
    const { user } = this.props;
    const { filesSize, maxSizeUploadFile } = this.state;
    const attachments = [...this.state.messageAttachments];

    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const currentFileSize = file.size;
    const fileName = file.name;
    const fileNameWithoutSpaces = fileName.split(' ').join('_');
    const formData = new FormData();
    formData.append('file', file, fileNameWithoutSpaces);

    const allFilesSize = filesSize + currentFileSize;

    const safeSizeLimit = normalizeGeneralMessageAttachmentSizeLimit(maxSizeUploadFile);

    if (allFilesSize > safeSizeLimit) {
      const maxSizeUploadFileText = formatGeneralMessageAttachmentSize(safeSizeLimit);

      const errorText = `The message cannot be sent as the total size of attachments is too large.
Please ensure your attachments don't exceed ${maxSizeUploadFileText} and try sending a message again.`;

      this.setState({
        errorMessage: errorText,
        isError: true
      });
    } else {
      this.setState({
        isLoadingFile: true
      });

      uploadFileAllPath(user, formData)
        .then(data => {
          const { key, link } = data;

          attachments.push({
            fileName: fileNameWithoutSpaces,
            link,
            externalFileId: key,
            fileSize: currentFileSize
          });

          this.setState({
            messageAttachments: attachments,
            filesSize: allFilesSize,
            isLoadingFile: false
          });
        })
        .catch(error => {
          console.error(error);
          this.setState({
            isImageError: true,
            isLoadingFile: false
          });
        });
    }
  };

  onRemoveAttachment = (key: string) => {
    const { filesSize } = this.state;
    const attachments = [...this.state.messageAttachments];
    const attachmentIndex = attachments.findIndex(attachment => attachment.externalFileId === key);
    const attachment = attachments.find(attachment => attachment.externalFileId === key);

    attachments.splice(attachmentIndex, 1);

    const newFileSize = filesSize - propz.get(attachment, ['fileSize'], 0);

    this.setState({
      messageAttachments: attachments,
      filesSize: newFileSize
    });
  };

  onCloseImageError = () => {
    this.setState({
      isImageError: false
    });
  };

  onCloseErrorClick = event => {
    event.preventDefault();

    this.setState({
      isError: false,
      errorMessage: ''
    });
  };

  getSteps(): MESSAGE_CREATING_STEPS[] {
    return [MESSAGE_CREATING_STEPS.CONTENT, MESSAGE_CREATING_STEPS.PREVIEW];
  }

  getStepTitle(): string {
    const { currentStep } = this.state;
    const steps = this.getSteps();
    const currentStepName = steps[currentStep];

    switch (currentStepName) {
      case MESSAGE_CREATING_STEPS.CONTENT:
        return 'Notification content';

      case MESSAGE_CREATING_STEPS.PREVIEW:
        return 'Preview';

      default:
        return '';
    }
  }

  getAttachmentAsLinksString = () => {
    const { messageAttachments } = this.state;

    if (messageAttachments.length > 0) {
      const attachmentAsLink = messageAttachments
        .map((attachment, index) => {
          return `<div key=attachment_${index}><a href=${attachment.link} target="_blank">${attachment.fileName}</a></div>`;
        })
        .join('');

      return `<div>Attachments: ${attachmentAsLink}</div>`;
    } else {
      return '';
    }
  };

  onIsEmailEditorChange = event => {
    const isEmailEditorEnabled = event.target.checked;

    if (!isEmailEditorEnabled) {
      this.setState({
        messageEmailSubject: '',
        messageEmailBody: '',
        messageAttachments: [],
        isEmailEditorEnabled,
        isError: false,
        errorMessage: ''
      });
      return;
    }

    this.setState({
      isEmailEditorEnabled,
      isError: false,
      errorMessage: ''
    });
  };

  onIsPushEditorChange = event => {
    const isPushEditorEnabled = event.target.checked;

    if (!isPushEditorEnabled) {
      this.setState({
        messagePushBody: '',
        messagePushTitle: '',
        isPushEditorEnabled,
        isError: false,
        errorMessage: ''
      });
      return;
    }

    this.setState({
      isPushEditorEnabled,
      isError: false,
      errorMessage: ''
    });
  };

  onSubmitCustomNotificationsForm = () => {
    const {
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      includeUnionAdmins,
      submitAction,
      scheduleAt,
      messageAttachments
    } = this.state;

    const { schools, onSubmit } = this.props;

    const messageEmailBodyWithAttachment = messageEmailBody + this.getAttachmentAsLinksString();
    const messageHTMLEmailBody =
      messageEmailBody !== ''
        ? `${HTML_FIRST_PART_PAGE_CODE} ${messageEmailBodyWithAttachment} ${HTML_LAST_PART_PAGE_CODE}`
        : '';

    const data = {
      schoolIds: schools.map(school => school.id),
      title: messageEmailSubject,
      text: messageEmailBody !== '' ? messageHTMLEmailBody : '',
      content: {
        push: { title: messagePushTitle, body: messagePushBody }
      },
      attachments: messageAttachments,
      includeUnionAdmins,
      submitAction,
      scheduleAt
    };

    onSubmit(data);
  };

  isNextButtonDisabled(): boolean {
    const {
      currentStep,
      isLoadingFile,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody
    } = this.state;

    const steps = this.getSteps();
    const currentStepName = steps[currentStep];

    const isAllEditorDisabled = !isPushEditorEnabled && !isEmailEditorEnabled;

    const isEmailInvalid =
      isEmailEditorEnabled && (!messageEmailSubject.trim() || !messageEmailBody || messageEmailBody.trim() === '');

    const isPushInvalid = isPushEditorEnabled && (!messagePushTitle.trim() || !messagePushBody.trim());

    switch (true) {
      case currentStepName === MESSAGE_CREATING_STEPS.CONTENT:
        return isLoadingFile || isAllEditorDisabled || isEmailInvalid || isPushInvalid;

      case currentStepName === MESSAGE_CREATING_STEPS.PREVIEW:
        return false;

      default:
        return false;
    }
  }

  getContentRequirements(): string[] {
    const {
      currentStep,
      isLoadingFile,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody
    } = this.state;

    if (this.getSteps()[currentStep] !== MESSAGE_CREATING_STEPS.CONTENT) {
      return [];
    }

    if (!isEmailEditorEnabled && !isPushEditorEnabled) {
      return ['Enable at least one delivery channel'];
    }

    const requirements: string[] = [];

    if (isEmailEditorEnabled) {
      if (!messageEmailSubject.trim()) requirements.push('Add an email subject');
      if (!messageEmailBody || !messageEmailBody.trim()) requirements.push('Add the email message');
    }

    if (isPushEditorEnabled) {
      if (!messagePushTitle.trim()) requirements.push('Add a push notification title');
      if (!messagePushBody.trim()) requirements.push('Add the push notification message');
    }

    if (isLoadingFile) requirements.push('Wait for the attachment to finish uploading');

    return requirements;
  }

  renderContentRequirements(): React.ReactNode {
    const requirements = this.getContentRequirements();

    if (requirements.length === 0) return null;

    return (
      <div className="tTournamentNotificationWizard__requirements" role="status" aria-live="polite">
        <span className="tTournamentNotificationWizard__requirementsIcon" aria-hidden="true">
          i
        </span>
        <div>
          <strong>Complete the following to continue:</strong>
          <ul>
            {requirements.map(requirement => (
              <li key={requirement}>{requirement}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  renderViewByStep(): React.ReactNode {
    const { currentStep } = this.state;
    const steps = this.getSteps();
    const currentStepName = steps[currentStep];

    switch (currentStepName) {
      case MESSAGE_CREATING_STEPS.CONTENT:
        return this.renderMessageTextForm();

      case MESSAGE_CREATING_STEPS.PREVIEW:
        return this.renderMessagePreview();

      default:
        return null;
    }
  }

  renderMessageTextForm(): React.ReactNode {
    const {
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      messageAttachments,
      isImageError,
      isError,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      isLoadingFile
    } = this.state;

    return (
      <div className="tTournamentNotificationWizard__step">
        <TournamentCustomNotificationTextForm
          user={this.props.user}
          recipientDescription={this.props.recipientDescription}
          onEmailSubjectChange={this.onEmailSubjectChange}
          onEmailBodyChange={this.onEmailBodyChange}
          onEmailBodyEmptyTextChange={this.onEmailBodyEmptyTextChange}
          onPushTitleChange={this.onPushTitleChange}
          onPushBodyChange={this.onPushBodyChange}
          onEmailAttachmentChange={this.onEmailAttachmentChange}
          onRemoveAttachment={this.onRemoveAttachment}
          isImageError={isImageError}
          isError={isError}
          onCloseImageError={this.onCloseImageError}
          messageEmailSubject={messageEmailSubject}
          messageEmailBody={messageEmailBody}
          messagePushTitle={messagePushTitle}
          messagePushBody={messagePushBody}
          messageAttachments={messageAttachments}
          isLoadingFile={isLoadingFile}
          isEmailEditorEnabled={isEmailEditorEnabled}
          isPushEditorEnabled={isPushEditorEnabled}
          onIsEmailEditorChange={this.onIsEmailEditorChange}
          onIsPushEditorChange={this.onIsPushEditorChange}
        />
      </div>
    );
  }

  renderMessagePreview(): React.ReactNode {
    const {
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      messageAttachments,
      includeUnionAdmins,
      submitAction,
      scheduleAt
    } = this.state;

    const { schools, tournament } = this.props;

    const unionName = (tournament && (tournament.school?.name || (tournament as any).schoolName)) || 'union';

    const isScheduleInvalid =
      submitAction === 'SCHEDULE' &&
      (!scheduleAt || Number.isNaN(new Date(scheduleAt).getTime()) || new Date(scheduleAt).getTime() <= Date.now());

    return (
      <div className="tTournamentNotificationWizard__previewStep">
        <TournamentCustomNotificationPreview
          messageEmailSubject={messageEmailSubject}
          messageEmailBody={messageEmailBody}
          messagePushTitle={messagePushTitle}
          messagePushBody={messagePushBody}
          messageAttachments={messageAttachments}
          isEmailEnabled={this.state.isEmailEditorEnabled}
          isPushEnabled={this.state.isPushEditorEnabled}
          affectedSchools={schools}
          includeUnionAdmins={includeUnionAdmins}
          unionName={unionName}
          onToggleIncludeUnionAdmins={next => this.setState({ includeUnionAdmins: next })}
          submitAction={submitAction}
          scheduleAt={scheduleAt}
          onSubmitActionChange={next =>
            this.setState({
              submitAction: next
            })
          }
          onScheduleAtChange={next =>
            this.setState({
              scheduleAt: next
            })
          }
          isScheduleInvalid={isScheduleInvalid}
        />
      </div>
    );
  }

  renderSendingError(): React.ReactNode {
    const { errorMessage, isError } = this.state;

    if (!isError) {
      return null;
    }

    return (
      <div className="alert fade show alert-danger d-flex justify-content-between align-items-center" role="alert">
        {errorMessage}
        <button
          type="button"
          className="ml-3 btn btn-danger"
          aria-label="Close"
          onClick={event => this.onCloseErrorClick(event)}
        >
          Close
        </button>
      </div>
    );
  }

  render() {
    const stepCount = this.getSteps().length;
    const { currentStep, isError, isLoading, submitAction, scheduleAt } = this.state;
    const hasNext = currentStep + 1 < stepCount;
    const hasPrev = currentStep > 0;
    const isNextButtonDisabled = this.isNextButtonDisabled();

    const isScheduleInvalid =
      submitAction === 'SCHEDULE' &&
      (!scheduleAt || Number.isNaN(new Date(scheduleAt).getTime()) || new Date(scheduleAt).getTime() <= Date.now());

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="tTournamentNotificationWizard">
        {this.renderSendingError()}

        <div className="tTournamentNotificationWizard__wizardTopbar">
          <div className="tTournamentNotificationWizard__steps" aria-label="Notification steps">
            {['Content', 'Delivery'].map((label, index) => (
              <div
                key={label}
                className={`tTournamentNotificationWizard__stepIndicator ${
                  index === currentStep ? 'is-active' : index < currentStep ? 'is-done' : ''
                }`}
              >
                <span>{index < currentStep ? '✓' : index + 1}</span>
                <b>{label}</b>
              </div>
            ))}
          </div>
        </div>

        {this.renderViewByStep()}

        {this.renderContentRequirements()}

        <div className="tTournamentNotificationWizard__actions">
          <div>
            <button type="button" className="is-ghost" onClick={this.props.onCancel}>
              Cancel
            </button>
          </div>
          <div>
            {hasPrev && (
              <button type="button" className="is-secondary" onClick={this.onPrevClick}>
                Previous
              </button>
            )}

            {hasNext ? (
              <button type="button" className="is-primary" onClick={this.onNextClick} disabled={isNextButtonDisabled}>
                Next
              </button>
            ) : (
              <button
                type="button"
                className="is-primary"
                onClick={this.onSubmitCustomNotificationsForm}
                disabled={isNextButtonDisabled || isError || isScheduleInvalid}
              >
                {submitAction === 'SCHEDULE' ? 'Schedule' : 'Send'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
